import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Checks if an API error happened because the server is offline or unreachable.
 *
 * - Returns TRUE for: No internet connection ('FETCH_ERROR'), requests taking too long
 * ('TIMEOUT_ERROR'), or backend server crashes (Status 500-599).
 * - Returns FALSE for: Definitive rejections like "Unauthorized" (401) or "Bad Request" (4xx).
 *
 * Why we need this:
 * During a cold start (pressing F5), if the internet drops, we don't want to accidentally
 * log the user out and wipe their session. Instead, we use this function to detect the outage
 * and show a friendly "Server Unreachable - Click to Retry" screen, keeping their session safe.
 */
export const isUnreachableError = (error: unknown): boolean => {
  const status = (error as FetchBaseQueryError | undefined)?.status;

  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') {
    return true;
  }

  // Check if it's a 5xx error (e.g., 500 Internal Server Error, 502 Bad Gateway, 503 Service Unavailable)
  return typeof status === 'number' && status >= 500 && status < 600;
};
